import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MenuComponent } from '../../../_metronic/assets/ts/components';
import { KTCard } from '../../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../../_metronic/helpers/components/KTCardBody';
import Content from '../../../_metronic/layout/components/content/Content';
import { PageTitle } from '../../../_metronic/layout/core';
import { ToolbarWrapper } from '../../../_metronic/layout/components/toolbar';
import { storeActivityIndicator, storeAreYouSureModalCallback } from '../../../redux/reducers/authSlice';
import TableFooter from '../../../components/TableFooter';
import StatusBadge from '../../../components/StatusBadge';
import ColumnFilterRow from '../../../components/ColumnFilter/ColumnFilterRow';
import ColumnFilterChips from '../../../components/ColumnFilter/ColumnFilterChips';
import { ColumnFilterDefinitions } from '../../../components/ColumnFilter/ColumnFilterTypes';
import { ColumnChooserModel } from '../../../models/common/ColumnChooserModel';
import { kolonlariBirlestir } from '../../../components/ColumnFilter/kolonBirlestir';
import { ColumnFilterModel } from '../../../models/common/ColumnFilterModel';
import { useSortableColumns } from '../../../helpers/useSortableColumns';
import { JobQueueService } from '../../../services/JobQueueService';
import { GetJobQueueListPagingAndFilterRequest, initialGetJobQueueListPagingAndFilterRequest } from '../../../models/request/jobQueue/GetJobQueueListPagingAndFilterRequest';
import { GetJobQueueListPagingAndFilterResponse, GetJobQueueListPagingAndFilterResponseData, GetJobQueueListPagingAndFilterResponseItem, initialGetJobQueueListPagingAndFilterResponseData, JobQueueCountResponse, JOB_QUEUE_STATUS } from '../../../models/response/jobQueue/JobQueueResponses';
import JobQueueListFilters from './Components/JobQueueListFilters';

/** Bekleyen ya da çalışan iş varken liste bu aralıkla sessizce yenilenir. */
const AUTO_REFRESH_MS = 5000

/**
 * Liste boşken ya da tüm işler bittiğinde kullanılan daha seyrek aralık.
 * Ekran işler oluşmadan açıldığında liste boş kalıyor ve yenileme hiç başlamıyordu;
 * kullanıcı sayfayı elle yenilemeden yeni işleri göremiyordu.
 */
const IDLE_REFRESH_MS = 30000

const JobQueueList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const pageName = "Ayarlar > İş Kuyruğu"
    const pageVersion = "1";
    const batchIdFromUrl = new URLSearchParams(location.search).get('batchId') ?? undefined;

    const [data, setData] = useState<GetJobQueueListPagingAndFilterResponseData>(initialGetJobQueueListPagingAndFilterResponseData);
    const [filter, setFilter] = useState<GetJobQueueListPagingAndFilterRequest>({ ...initialGetJobQueueListPagingAndFilterRequest, batchId: batchIdFromUrl })

    const getColumns = () => {
        var list: ColumnChooserModel[] = [];

        var str: string | null = null;

        try {
            str = localStorage.getItem(pageName + "-" + pageVersion + "-filters");
        } catch { }

        list = [
            { title: 'Kuyruğa Alınma', name: 'createdDateStr', order: 0, isVisible: true },
            { title: 'İş', name: 'jobTypeName', order: 0, isVisible: true },
            { title: 'Kayıt', name: 'entityRefNo', order: 0, isVisible: true },
            { title: 'Durum', name: 'statusName', order: 0, isVisible: true },
            { title: 'Deneme', name: 'attemptCount', order: 0, isVisible: true },
            { title: 'Sonuç / Hata', name: 'resultMessage', order: 0, isVisible: true },
            { title: 'Başlama', name: 'startedDateStr', order: 0, isVisible: false },
            { title: 'Bitiş', name: 'finishedDateStr', order: 0, isVisible: true },
            { title: 'Sonraki Deneme', name: 'nextRunAtStr', order: 0, isVisible: true },
            { title: 'Kuyruğa Alan', name: 'createdUser', order: 0, isVisible: true },
        ];

        for (var i = 0; i < list.length; i++)
            list[i].order = i + 1;

        return kolonlariBirlestir(list, str);
    }

    const [columns, setColumns] = useState<ColumnChooserModel[]>(getColumns());

    // Kolon filtresinde art arda istek gidebilir; yalnızca en son isteğin cevabı ekrana yazılır.
    const lastRequestNo = useRef(0);

    const getData = async (_filter: GetJobQueueListPagingAndFilterRequest | undefined = undefined, silent: boolean = false) => {
        const requestNo = ++lastRequestNo.current;

        try {
            if (_filter)
                setFilter(_filter);
            else
                _filter = filter;

            if (!silent)
                dispatch(storeActivityIndicator(true))

            var response: GetJobQueueListPagingAndFilterResponse = await JobQueueService.getJobQueueListPagingAndFilter(_filter);

            if (requestNo != lastRequestNo.current)
                return;

            if (!response.isSuccess) {
                if (!silent)
                    toast.error(response.message)
                return;
            }

            // Sessiz yenilemede seçimler korunur.
            setData(prev => {
                const selected = new Set(prev.items.filter(x => x.isSelected).map(x => x.jobQueueId));
                return { ...response.data, items: response.data.items.map(x => ({ ...x, isSelected: selected.has(x.jobQueueId) })) };
            });
        } catch (error: any) {
            if (!silent)
                toast.error(error)
        }
        finally {
            if (!silent && requestNo == lastRequestNo.current)
                dispatch(storeActivityIndicator(false))
        }
    }

    useEffect(() => {
        setTimeout(() => { MenuComponent.reinitialization() }, 1000)
    }, [])

    // İlerleme kutusundaki "Ayrıntı" aynı sayfadayken de çalışsın.
    useEffect(() => {
        getData({ ...filter, batchId: batchIdFromUrl, currentPage: 1, skip: 0 });
    }, [batchIdFromUrl])

    const hasActive = data.items.some(x => x.status == JOB_QUEUE_STATUS.BEKLIYOR || x.status == JOB_QUEUE_STATUS.CALISIYOR);

    useEffect(() => {
        const timer = setInterval(() => getData(undefined, true), hasActive ? AUTO_REFRESH_MS : IDLE_REFRESH_MS);
        return () => clearInterval(timer);
    }, [hasActive, filter])

    const performPagination = (i: number) => getData({ ...filter, currentPage: i, skip: (i - 1) * filter.take });

    const changeTake = (_take: number) => getData({ ...filter, currentPage: 1, skip: 0, take: _take });

    const selectedItems = data.items.filter(x => x.isSelected);
    const retryItems = selectedItems.filter(x => x.status == JOB_QUEUE_STATUS.HATALI || x.status == JOB_QUEUE_STATUS.IPTAL);
    const cancelItems = selectedItems.filter(x => x.status == JOB_QUEUE_STATUS.BEKLIYOR);

    const runAction = async (action: () => Promise<JobQueueCountResponse>) => {
        try {
            dispatch(storeActivityIndicator(true))

            var response: JobQueueCountResponse = await action();

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            if (response.data > 0)
                toast.success(response.message)
            else
                toast.error(response.message)

            await getData({ ...filter });
        } catch (error: any) {
            toast.error(error)
        }
        finally {
            dispatch(storeActivityIndicator(false))
        }
    }

    const retryJobs = () => {
        const run = () => runAction(() => JobQueueService.retryJobs(retryItems.map(x => x.jobQueueId)));

        // GİB gönderimi geri alınamaz: yeniden denemeden önce onay alınır.
        if (retryItems.some(x => x.jobType == 'UYUMSOFT_IRSALIYE_GIB'))
            dispatch(storeAreYouSureModalCallback(run))
        else
            run();
    }

    const cancelJobs = () => runAction(() => JobQueueService.cancelJobs(cancelItems.map(x => x.jobQueueId)));

    const setItemSelected = (_id: string) => {
        setData({ ...data, items: data.items.map(x => x.jobQueueId == _id ? { ...x, isSelected: !x.isSelected } : x) })
    }

    const selectAll = () => {
        const isAllSelected = data.items.length > 0 && data.items.every(x => x.isSelected);
        setData({ ...data, items: data.items.map(x => ({ ...x, isSelected: !isAllSelected })) })
    }

    const openEntity = (item: GetJobQueueListPagingAndFilterResponseItem) => {
        if (item.entityId && item.jobType.startsWith('UYUMSOFT_IRSALIYE'))
            navigate('/ambar/listadd?id=' + item.entityId);
    }

    const clearBatch = () => navigate('/jobqueue/list');

    const sortableColumns = ['createdDateStr', 'jobTypeName', 'entityRefNo', 'statusName', 'attemptCount', 'resultMessage', 'startedDateStr', 'finishedDateStr', 'nextRunAtStr', 'createdUser'];

    const { thProps, sortArrow } = useSortableColumns(sortableColumns, filter, getData);

    // Kolon adları backend'deki eşlemeyle (JobQueueManager.ApplyColumnFilters) aynı.
    const columnFilterDefinitions: ColumnFilterDefinitions = {
        createdDateStr: { type: 'date' },
        jobTypeName: {
            type: 'select', allowEmpty: false, loadOptions: () => Promise.resolve([
                { value: 'UYUMSOFT_IRSALIYE_TASLAK', label: 'E-İrsaliye Taslak (Uyumsoft)' },
                { value: 'UYUMSOFT_IRSALIYE_GIB', label: 'E-İrsaliye GİB (Uyumsoft)' },
            ])
        },
        entityRefNo: { type: 'text' },
        statusName: {
            type: 'select', allowEmpty: false, renderOption: (label) => <StatusBadge text={label} />, loadOptions: () => Promise.resolve([
                { value: String(JOB_QUEUE_STATUS.BEKLIYOR), label: 'Bekliyor' },
                { value: String(JOB_QUEUE_STATUS.CALISIYOR), label: 'Çalışıyor' },
                { value: String(JOB_QUEUE_STATUS.BASARILI), label: 'Başarılı' },
                { value: String(JOB_QUEUE_STATUS.HATALI), label: 'Hatalı' },
                { value: String(JOB_QUEUE_STATUS.IPTAL), label: 'İptal' },
            ])
        },
        attemptCount: { type: 'number' },
        resultMessage: { type: 'text' },
        startedDateStr: { type: 'date' },
        finishedDateStr: { type: 'date' },
        nextRunAtStr: { type: 'date' },
        createdUser: { type: 'text' },
    };

    const changeColumnFilters = (columnFilters: ColumnFilterModel[]) => {
        getData({ ...filter, columnFilters: columnFilters, currentPage: 1, skip: 0 });
    }

    const getColumnValue = (item: GetJobQueueListPagingAndFilterResponseItem, column: string, className: string | undefined) => {
        const tdProps = { className: className, onClick: () => setItemSelected(item.jobQueueId), onDoubleClick: () => openEntity(item) };

        if (column == "statusName")
            return <td {...tdProps}><StatusBadge text={item.statusName} /></td>;

        if (column == "attemptCount")
            return <td {...tdProps}>{item.attemptCount}/{item.maxAttempts}</td>;

        if (column == "resultMessage")
            return <td {...tdProps} className={(className ?? '') + (item.status == JOB_QUEUE_STATUS.HATALI ? ' text-danger' : '')} style={{ whiteSpace: 'normal', minWidth: 260 }}>{item.resultMessage}</td>;

        var item2: any = item;
        return <td {...tdProps}>{item2[column]}</td>;
    }

    const visibleColumns = columns.filter(x => x.isVisible).sort(function (a, b) { return a.order - b.order; });

    return (
        <React.Fragment>
            <ToolbarWrapper filters={
                <JobQueueListFilters
                    columns={columns}
                    getColumns={getColumns}
                    pageName={pageName}
                    pageVersion={pageVersion}
                    setColumns={setColumns}
                    retryCount={retryItems.length}
                    cancelCount={cancelItems.length}
                    retryFunction={retryJobs}
                    cancelFunction={cancelJobs}
                    refreshFunction={() => getData()}
                />
            } />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Content>
                <KTCard className='mb-3'>
                    <KTCardBody className='py-2'>
                        {
                            filter.batchId && (
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                    <span className='badge badge-light-primary fs-7'>Yalnız seçilen toplu işlem gösteriliyor</span>
                                    <a href='#' className='fs-7' onClick={(e) => { e.preventDefault(); clearBatch(); }}>Tüm işleri göster</a>
                                </div>
                            )
                        }

                        <ColumnFilterChips
                            columns={columns}
                            definitions={columnFilterDefinitions}
                            filters={filter.columnFilters}
                            onChange={changeColumnFilters}
                        />

                        <div className='table-responsive new-table-container'>
                            <table className='table align-middle table-row-dashed dataTable no-footer ebbe-compact-table'>
                                <thead>
                                    <tr className='text-start text-muted fw-bolder text-uppercase'>
                                        <th style={{ width: 1 }}>
                                            <div className='d-flex'>
                                                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                                    <input onChange={selectAll} className='form-check-input' type='checkbox' checked={data.items.length > 0 && data.items.every(x => x.isSelected)} />
                                                </label>
                                            </div>
                                        </th>

                                        {
                                            visibleColumns.map((item) =>
                                                <th key={item.name} {...thProps(item.name)}>
                                                    {item.title}
                                                    {sortArrow(item.name)}
                                                </th>
                                            )
                                        }
                                    </tr>

                                    <ColumnFilterRow
                                        columns={visibleColumns}
                                        definitions={columnFilterDefinitions}
                                        filters={filter.columnFilters}
                                        onChange={changeColumnFilters}
                                        leadingCellCount={1}
                                    />
                                </thead>
                                <tbody className='text-gray-600 fw-bold'>
                                    {
                                        data.items.length == 0 && (
                                            <tr>
                                                <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                            </tr>
                                        )
                                    }
                                    {
                                        data.items.map((item) =>
                                            <tr key={item.jobQueueId} style={{ background: item.isSelected ? 'rgba(204, 204, 204, 0.25)' : undefined }}>
                                                <td style={{ width: 1 }}>
                                                    <div className='d-flex'>
                                                        <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                                            <input onChange={() => setItemSelected(item.jobQueueId)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                        </label>
                                                    </div>
                                                </td>

                                                {
                                                    visibleColumns.map((column) => (
                                                        <React.Fragment key={column.name}>{getColumnValue(item, column.name, column.className)}</React.Fragment>
                                                    ))
                                                }
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </KTCardBody>

                    <TableFooter
                        changeTake={changeTake}
                        take={filter.take}
                        currentPage={filter.currentPage}
                        performPagination={performPagination}
                        totalPageCount={Math.ceil(data.totalRecordCount / filter.take)}
                        totalRecordCount={data.totalRecordCount}
                        selectedCount={selectedItems.length}
                    />
                </KTCard>
            </Content>
        </React.Fragment>
    )
}

export default JobQueueList;
